import { ButtonDropdown, Color, Divider, Icon, NavItem } from '@billy/design-system'
import { useCallback, useState } from 'react'

import { InputNumberLocalized, InputNumberLocalizedProps } from '../InputNumberLocalized'
import * as Styled from './styles'

export interface SplitNumberInputProps<T = string> extends InputNumberLocalizedProps {
  dropdownMinWidth?: string
  dropdownOptions: NavItem<T>[]
  onOptionChange: (value: T) => void
  selectedOption: T
}

export const SplitNumberInput = <T extends string | number>({
  disabled,
  dropdownMinWidth,
  dropdownOptions,
  error,
  onOptionChange,
  selectedOption,
  ...inputProps
}: SplitNumberInputProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleSelect = useCallback(
    (_: string, value: T) => {
      onOptionChange(value)
    },
    [onOptionChange],
  )

  return (
    <Styled.SplitNumberInputWrapper $disabled={disabled} $error={error}>
      <Styled.InputSection>
        <InputNumberLocalized
          {...inputProps}
          alignment="left"
          bordered={false}
          disabled={disabled}
          // We don't pass error to InputNumberLocalized to avoid it rendering its own
          // red border, which would visually conflict with the wrapper's unified error border.
          // Instead we replicate the DS error icon manually via suffix.
          suffix={error ? <Icon color={Color.Red700} icon="circleAlert" size="sm" /> : undefined}
        />
      </Styled.InputSection>
      <Divider direction="vertical" dividerColor={Color.Grey200} />
      <ButtonDropdown
        dropdownSize="fitTrigger"
        items={dropdownOptions}
        onClose={handleClose}
        onOpen={handleOpen}
        onSelect={handleSelect}
        placement="bottom-end"
        selectedId={String(selectedOption)}
        triggerElement={
          <Styled.DropdownTrigger $isOpen={isOpen} $minWidth={dropdownMinWidth} disabled={disabled} type="button">
            {dropdownOptions.find((option) => option.value === selectedOption)?.children}
            <Icon color="currentColor" icon={isOpen ? 'chevronUp' : 'chevronDown'} size="sm" />
          </Styled.DropdownTrigger>
        }
      />
    </Styled.SplitNumberInputWrapper>
  )
}
